"use client";

import {
  useEffect,
  useRef,
  useState,
  type ElementType,
  type ReactNode,
} from "react";

type Props = {
  children: ReactNode;
  /** Element/tag to render. Defaults to a plain div. */
  as?: ElementType;
  /**
   * Stagger the direct children into view one after another instead of fading
   * the whole block as a single unit. Use for grids / lists.
   */
  stagger?: boolean;
  /** Extra delay (ms) before the block reveals. Ignored when `stagger` is set. */
  delay?: number;
  /** Extra classes merged onto the wrapper. */
  className?: string;
  /** Pass-through landmark attributes so semantic elements stay labelled. */
  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
};

/**
 * Scroll-reveal wrapper. The element starts hidden (via the `.js`-gated CSS in
 * globals.css) and fades + rises into view the first time it intersects the
 * viewport. The hidden state is gated behind `.js` so crawlers and no-JS
 * visitors always see the content, and the `prefers-reduced-motion` block in
 * globals.css forces it fully visible with no motion.
 */
export default function Reveal({
  children,
  as: Tag = "div",
  stagger = false,
  delay = 0,
  className = "",
  ...rest
}: Props) {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el && typeof IntersectionObserver !== "undefined") {
      const io = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.disconnect();
          }
        },
        { rootMargin: "0px 0px -10% 0px", threshold: 0.15 },
      );
      io.observe(el);
      return () => io.disconnect();
    }
    // Fallback for environments without IntersectionObserver: reveal on the
    // next frame. Deferring keeps the setState out of the effect body (avoids
    // the cascading-render lint) and prevents any first-paint flash.
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const base = stagger ? "reveal-stagger" : "reveal";

  return (
    <Tag
      ref={ref}
      className={`${base}${visible ? " is-visible" : ""}${className ? ` ${className}` : ""}`}
      style={!stagger && delay ? { transitionDelay: `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
}
